import React from 'react';
import ContainerDimensions from 'react-container-dimensions';
import journalize from 'journalize';

import { Tooltip } from 'react-tippy';
import { startCase, toLower, range, sortBy } from 'lodash';

import { geoMercator, geoPath } from 'd3-geo';
import { scaleSequential } from 'd3-scale';
import { interpolateOranges } from 'd3-scale-chromatic';

import FaHandPointerO from 'react-icons/lib/fa/hand-pointer-o';

import 'react-tippy/dist/tippy.css';

const formatPercent = function(value) {
  const formatted = Math.round(parseFloat(value) * 100, 2);
  return `${formatted}%`;
}

const formatLargeInt = function(value) {
  return journalize.intcomma(parseInt(value.replace("$", "").replace(",", "").trim()));
}

const formatTicketRate = function(value) {
  const formatted = Math.round(parseFloat(value), 2);
  return `${formatted}%`;
}

const formatLongTicketRate = function(value) {
  const formatted = (parseFloat(value)).toFixed(1);
  return `${formatted}`;
}

const formatCommunityLabel = function(value) {
  return startCase(toLower(value)).replace("Oh", "O'H");
}

class TooltipLabel extends React.Component {
  render() {
    const { TOT_POP, TOT_HH, MEDINC, black_pop, white_pop, hisp_pop, ticket_rate, ticket_count, community } = this.props;

    const communityLabel = formatCommunityLabel(community);
    const ticketRate = formatLongTicketRate(ticket_rate);
    const ticketCount = formatLargeInt(ticket_count);
    const totalPopulation = formatLargeInt(TOT_POP);
    const totalHouseholds = formatLargeInt(TOT_HH);
    const medianIncome = formatLargeInt(MEDINC);
    const blackPopulationPercent = formatPercent(black_pop);
    const whitePopulationPercent = formatPercent(white_pop);
    const hispanicPopulationPercent = formatPercent(hisp_pop);

    return (<div>
      <h1>{communityLabel}</h1>

      {(ticketRate * 10 > 1) &&
        <p>There were <strong>{ticketRate *  10} duplicate tickets</strong> issued for every 1,000 households in {communityLabel} from 2011 through 2015.</p>
      }
      {(ticketRate * 10 == 1) &&
        <p>There was <strong>{ticketRate *  10} duplicate ticket</strong> issued for every 1,000 households in {communityLabel} from 2011 through 2015.</p>
      }
      <div className="group l-40-60">
        <div className="col p-less">
          <table>
            <tbody>
              <tr>
                <td className="value">{blackPopulationPercent}</td>
                <td className="label">BLACK</td>
              </tr>
              <tr>
                <td className="value">{hispanicPopulationPercent}</td>
                <td className="label">HISPANIC</td>
              </tr>
              <tr>
                <td className="value">{whitePopulationPercent}</td>
                <td className="label">WHITE</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="col p-more">
          <table>
            <tbody>
              <tr>
                <td className="value">{ticketCount}</td>
                <td className="label">duplicate ticket instances</td>
              </tr>
              <tr>
                <td className="value">{totalHouseholds}</td>
                <td className="label">households</td>
              </tr>
              <tr>
                <td className="value">${medianIncome}</td>
                <td className="label">median income</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>)
  }
}

class TicketMap extends React.Component {
  state = {}

  projection = geoMercator();
  colorScale = scaleSequential(interpolateOranges).domain([-0.5, 3.1])

  render() {
    const { projection, colorScale } = this;
    const { ticketData } = this.props;
    return (<div>

      <h1>Black Neighborhoods See the Highest Rates of Duplicate Ticketing</h1>

      <p>Ten of Chicago’s 77 community areas have duplicate ticket rates that are more than 4.5 times the median for the city. These community areas are all more than 80 percent black.</p>

      <div className="group l-25-75">
        <div className="col p-less">
          <div className="scale">
              <h3>Rate of duplicate ticketing</h3>
              <div className="scale-boxes">
                {range(0, 3, 0.5).map( (value, i) => (
                  <div 
                    className="scale-box"
                    key={'scale-box'+i}
                    style={{
                      background: colorScale(value),
                    }}
                  />
                ))}
              </div>
              <div className="scale-labels">
                <div className="scale-min-label scale-label">
                  0%
                </div>
                <div className="scale-max-label scale-label">
                  3%
                </div>
              </div>
            </div>
      
          <div className="aspect-ratio-box">
            <div className="aspect-ratio-box-inside">
              <p className="hint"><FaHandPointerO /> Hover or tap for details</p>
              <ContainerDimensions>
                { ({ width, height }) => {
                  projection.fitSize([width, height], ticketData)
                  const path = geoPath(projection);
                  return (
                    <svg width={ width } height={ height }>
                      {
                        ticketData.features.map((d,i) => (
                          <Tooltip
                            key={'tooltip-'+i}
                            tag="g"
                            html={<TooltipLabel {...d.properties} />}
                            arrow={true}
                            animation="fade"
                          >
                            <path
                              key={'path-' + i}
                              d={path(d)}
                              className="community-area"
                              style={{ fill: colorScale(parseInt(d.properties.ticket_rate)) }}
                            ></path>
                          </Tooltip>
                        ))
                      }
                    </svg>
                  )
                }}
              </ContainerDimensions>
            </div>
          </div>

        </div>
        <div className="col p-more">
          <div className="legend">
            <h2>Neighborhoods with Duplicate Ticket Rates Over 1.8%</h2>
            <table>
              <thead>
                <tr>
                  <th>Community</th>
                  <th className="number">Rate</th>
                  <th className="number hide-mobile">Black</th>
                  <th className="number hide-mobile">White</th>
                  <th className="number hide-mobile">Hispanic</th>
                </tr>
              </thead>
              <tbody>
                {sortBy(ticketData.features, (f) => (parseFloat(f.properties.ticket_rate))).reverse().slice(0, 10).map( (item, i) => (
                <tr key={"row"+i}>
                  <td>
                    {formatCommunityLabel(item.properties.community)}
                  </td>
                  <td className="number">
                    {formatLongTicketRate(item.properties.ticket_rate)}%
                  </td>
                  <td className="number hide-mobile">
                    {formatPercent(item.properties.black_pop)}
                  </td>
                  <td className="number hide-mobile">
                    {formatPercent(item.properties.white_pop)}
                  </td>
                  <td className="number hide-mobile">
                    {formatPercent(item.properties.hisp_pop)}
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>)
  }
}

export default TicketMap



// WEBPACK FOOTER //
// ./src/components/viz/TicketMap/index.js